import { Injectable } from '@angular/core';
import { Actions, createEffect, ofType } from '@ngrx/effects';

import { CoreGoogleAnalyticActions } from '@fantaz/core/analytic';
import { RouterSelectors } from '@fantaz/core/router';
import {
  CoreDialogMessageActions,
  CoreSnackbarActions,
} from '@fantaz/core/state';
import { LocationService } from '@fantaz/core/utils';
import { AuthSelectors } from '@fantaz/shared/auth';
import {
  FzGoogleTagManagerService,
  TournamentApiService,
} from '@fantaz/shared/data-access/api';
import {
  GAEventKeys,
  TournamentPass,
  UserPasses,
} from '@fantaz/shared/data-access/models';
import { ToastService } from '@fantaz/shared/utils/toast';
import { UserHasPassAvailableComponent } from '@fantaz/tournaments/ui/error-messages';
import { Store } from '@ngrx/store';
import { of } from 'rxjs';
import {
  catchError,
  delay,
  filter,
  map,
  mergeMap,
  switchMap,
  withLatestFrom,
} from 'rxjs/operators';
import * as TournamentDetailsActions from '../casual-tournament-details';
import { PlayTournamentActions } from '../play-tournament';
import * as PassesActions from './passes.actions';
import * as PassesSelectors from './passes.selectors';

@Injectable()
export class PassesEffects {
  /**
   * Effect to load tournament passes after user load tournaments details
   */
  loadPassesAfterLoadTournamentDetailsEffect$ = createEffect(() =>
    this.actions$.pipe(
      ofType(TournamentDetailsActions.loadCasualTournamentDetailsSuccess),
      withLatestFrom(
        this.store.select(RouterSelectors.selectRouteParam('eventId')),
        (payload, eventId) => ({
          ...payload,
          eventScheduleUuid: eventId,
        }),
      ),
      switchMap(({ casualTournamentDetails, eventScheduleUuid }) => {
        if (!eventScheduleUuid) {
          return [];
        }
        return [
          PassesActions.loadTournamentPasses({
            eventPrototypeUuid: casualTournamentDetails.eventPrototypeUuid,
            eventScheduleUuid: eventScheduleUuid,
          }),
        ];
      }),
    ),
  );

  /**
   * Effect to load tournament passes
   */
  loadPassesEffects$ = createEffect(() => {
    return this.actions$.pipe(
      ofType(PassesActions.loadTournamentPasses),
      switchMap(
        (options: {
          eventPrototypeUuid: string;
          eventScheduleUuid: string;
          userId?: string;
        }) => {
          return this.tournamentApiService.getTournamentPasses(options).pipe(
            delay(1000), // wait for animation
            map((data: TournamentPass[]) => {
              return PassesActions.loadPassesSuccess({
                passes: data,
              });
            }),
            catchError((error) => {
              return of(PassesActions.loadPassesFailure({ error }));
            }),
          );
        },
      ),
    );
  });

  /**
   * Effect to refresh passes list. To refresh passes list we need to get the
   * latest query params from the store and call `loadTournamentPasses` action
   */
  refreshPasses$ = createEffect(() =>
    this.actions$.pipe(
      ofType(PassesActions.refreshPasses),
      withLatestFrom(this.store.select(PassesSelectors.selectQueryParams)),
      mergeMap(([_, options]) =>
        this.tournamentApiService
          .getTournamentPasses({
            eventPrototypeUuid: options?.eventPrototypeUuid as string,
            eventScheduleUuid: options?.eventScheduleUuid as string,
          })
          .pipe(
            map((data: TournamentPass[]) => {
              return PassesActions.loadPassesSuccess({
                passes: data,
              });
            }),
            catchError((error) => {
              return of(PassesActions.loadPassesFailure({ error }));
            }),
          ),
      ),
    ),
  );

  buyPassEffect$ = createEffect(() =>
    this.actions$.pipe(
      ofType(PassesActions.buyTournamentPass),
      withLatestFrom(this.store.select(AuthSelectors.getCurrentUser)),
      switchMap(([options]) =>
        this.locationService.observeLocation().pipe(
          switchMap((location) =>
            this.tournamentApiService
              .buyTournamentPass({
                eventPrototypeUuid: options.eventPrototypeUuid,
                fantazPassUuid: options.fantazPassUuid,
                eventScheduleUuid: options.eventScheduleUuid,
                passIndex: options.passIndex,
                userLocation: location,
              })
              .pipe(
                switchMap((pass: UserPasses) => {
                  // notify user
                  this.toastService.openSnackBar(
                    'Now you can start playing in the tournament using the pass',
                  );
                  // notify reducer
                  return [
                    PassesActions.buyPassSuccess({
                      pass,
                      fantazPassUuid: options.fantazPassUuid,
                      playOnBuy: options.playOnBuy,
                      meta: {
                        tournamentName: options.tournamentName,
                        gameName: options.gameName,
                      },
                    }),
                  ];
                }),
                catchError((error) => [
                  PassesActions.buyPassFailure({
                    error,
                    fantazPassUuid: options.fantazPassUuid,
                  }),
                ]),
              ),
          ),
        ),
      ),
    ),
  );

  /**
   * Effect to play tournament when user buy a pass
   */
  handleBuyPassSuccess$ = createEffect(() =>
    this.actions$.pipe(
      ofType(PassesActions.buyPassSuccess),
      mergeMap((action) => {
        return [
          // refresh pass list in background to get the latest passes list after
          // user buy a pass
          PassesActions.refreshPasses(),

          // open play tournament in case user want to play tournament after buy
          // a pass
          ...(action.playOnBuy
            ? [
                PlayTournamentActions.playTournament({
                  ...action.playOnBuy,
                  pass: action.pass,
                }),
              ]
            : []),

          // report to google analytics that user buy a pass for a tournament
          // and attach the tournament details to the event
          CoreGoogleAnalyticActions.trackEvent({
            event: {
              event:
                action.pass.price?.promoCodes &&
                action.pass.price?.promoCodes.length > 0
                  ? GAEventKeys.USER_BUYS_PASS_USING_CODE
                  : GAEventKeys.USER_BUYS_PASS,
              timestamp: Date.now(),
              tournament_id: action.pass.eventPrototypeUuid,
              tournament_name: action.meta.tournamentName,
              game_name: action.meta.gameName,
              type_of_tournament: 'casual',
              instance_id: action.pass.eventScheduleUuid,
              pass_id: action.pass.uuid,
              pass_name: action.pass.name,
              pass_price: action.pass.price.price,
              pass_entries: action.pass.entries,
              fantaz_pass_id: action.pass.fantazPassUuid,

              // attach promo code if exists
              ...(action.pass.price?.promoCodes &&
              action.pass.price?.promoCodes.length > 0
                ? {
                    promo_code: action.pass.price?.promoCodes[0].uuid,
                    promo_code_percent:
                      action.pass.price?.promoCodes[0].percent,
                  }
                : {}),
            },
          }),
        ];
      }),
    ),
  );

  /**
   * Effect to handle buy pass failure for codes:
   * LOCATION_NOT_ALLOWED: When user location is not allowed to buy a pass
   * NOT_ENOUGH_BALANCE: When user don't have enough balance to buy a pass
   * WALLET_FROZEN_BY_ADMIN: When user wallet is frozen by admin
   * WALLET_FROZEN_BY_TAX_INFORMATION_REQUIRED: When user wallet is frozen by tax information required
   */
  handleBuyPassFailureEffectForLocationAndBalance$ = createEffect(() =>
    this.actions$.pipe(
      ofType(PassesActions.buyPassFailure),
      filter(({ error }) =>
        [
          'LOCATION_NOT_ALLOWED',
          'NOT_ENOUGH_BALANCE',
          'WALLET_FROZEN_BY_ADMIN',
          'WALLET_FROZEN_BY_TAX_INFORMATION_REQUIRED',
        ].includes(error.message),
      ),
      switchMap(({ error }) => [
        CoreDialogMessageActions.showDialog({
          modalType: (error as unknown as Error).message,
          payload: {
            ...(error.message === 'NOT_ENOUGH_BALANCE' && {
              description: `I looks like you don't have enough funds in your Z-Wallet to buy the paid pass. Don't worry, you can easily add funds to continue. Please load your wallet and start having fun!`,
            }),
          },
        }),
      ]),
    ),
  );

  /**
   * Effect to handle buy pass failure for codes:
   * USER_HAS_PASSES_AVAILABLE: When user already have a pass for the tournament
   */
  handleBuyPassFailureEffect$ = createEffect(() =>
    this.actions$.pipe(
      ofType(PassesActions.buyPassFailure),
      filter(({ error }) =>
        ['USER_HAS_PASSES_AVAILABLE'].includes(error.message),
      ),
      switchMap(({ error }) => [
        CoreSnackbarActions.showSnackbar({
          message: UserHasPassAvailableComponent,
          config: {
            autoClose: false,
            data: {
              onClick: () => {
                this.store.dispatch(PassesActions.refreshPasses());
              },
            },
          },
        }),
      ]),
    ),
  );

  /**
   * Effect to handle buy pass failure for codes:
   * TOURNAMENT_NOT_AVAILABLE: When tournament is not available anymore (ended)
   * INVALID_FANTAZ_PASS: When pass is not valid
   */
  handleBuyPassFailureEffect3$ = createEffect(() =>
    this.actions$.pipe(
      ofType(PassesActions.buyPassFailure),
      filter(({ error }) =>
        ['TOURNAMENT_NOT_AVAILABLE', 'INVALID_FANTAZ_PASS'].includes(
          error.message,
        ),
      ),
      switchMap(({ error }) => {
        const mapMessageToModalType = {
          TOURNAMENT_NOT_AVAILABLE:
            'Oops! This tournament has ended. Stay tuned for more fun coming soon!',
          INVALID_FANTAZ_PASS:
            'Something went awry with the pass. Please try again or contact customer support if the issue persists',
        };

        return [
          CoreSnackbarActions.showSnackbar({
            message:
              mapMessageToModalType[
                error.message as keyof typeof mapMessageToModalType
              ],
            config: {
              dismissible: true,
              autoClose: false,
            },
          }),
        ];
      }),
    ),
  );

  /**
   * Effect to handle buy pass unknown of already bought pass failures
   * ALREADY_BOUGHT_PASS: The UI limits the user to try to buy again a redeemed pass so the probability of this error is low
   */
  handleBuyPassUnknownFailureEffect$ = createEffect(() =>
    this.actions$.pipe(
      ofType(PassesActions.buyPassFailure),
      filter(({ error }) =>
        ['UNKNOWN', 'ALREADY_BOUGHT_PASS'].includes(error.message),
      ),
      switchMap(() => [
        CoreSnackbarActions.showSnackbar({
          message: `Something went awry with the pass. Please try again or contact customer support if the issue persists.`,
          config: {
            dismissible: true,
            autoClose: false,
          },
        }),
      ]),
    ),
  );

  constructor(
    private readonly actions$: Actions,
    private tournamentApiService: TournamentApiService,
    private readonly toastService: ToastService,
    private store: Store,
    private gtmService: FzGoogleTagManagerService,
    private readonly locationService: LocationService,
  ) {}
}
